import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button } from '../../components/ui/button';
import { CenteredSpinner } from '../../components/ui/Spinner';
import { ApiError } from '../../lib/api';
import { computeDisplayName } from '../../types/contact';
import { listContacts } from '../contacts/api';
import { singleSendPreview, type PreviewSendChannel, type SendResult } from './sendApi';
import {
  computeRowSendEligibility,
  type ContactEligibility,
} from './computeRowSendEligibility';

// Ad Preview Stage 3 §3.1. Single-send modal -- row-scoped (approved
// clarification #1): sends ONLY this row's preview to the row's
// eligible contacts. Bulk aggregation across the rollout is the
// Master Grid's BulkSendPreviewModal.
//
// Eligibility is computed client-side from the contact data (the
// same two gates the server enforces): a contact is eligible when
// their AccountLink for THIS location opts in to campaign previews;
// per channel, email needs channelEmail + a non-empty email, SMS
// needs channelSMS + a non-empty phone. The server re-checks
// everything -- this is just the modal's preview of who'll receive.

type Props = {
  rolloutId: string;
  rowId: string;
  /** The row's location accountId -- used to scope the contact list +
   *  read the per-link receivesCampaignPreviews flag. */
  locationAccountId: string;
  locationName: string;
  /** The row's effective channels (campaign AND account AND row override).
   *  Neither on = not sendable. Optional: the server enforces the same gate. */
  rowChannels?: { meta: boolean; google: boolean };
  onClose: () => void;
  /** Fired with the send result so the parent can toast + refetch. */
  onSent: (result: SendResult) => void;
};

export function SendPreviewModal({
  rolloutId,
  rowId,
  locationAccountId,
  locationName,
  rowChannels,
  onClose,
  onSent,
}: Props) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const [error, setError] = useState<string | null>(null);
  // Per-contact include toggles, keyed by contact id. Default: all
  // eligible contacts checked.
  const [includedContacts, setIncludedContacts] = useState<Set<string>>(new Set());
  const [channels, setChannels] = useState<Set<PreviewSendChannel>>(new Set());
  const [initialised, setInitialised] = useState(false);

  const contactsQuery = useQuery({
    queryKey: ['contacts', locationAccountId],
    queryFn: () => listContacts(locationAccountId),
  });

  const { eligible, excluded, rowExcludedReason } = useMemo(() => {
    if (!contactsQuery.data) {
      return {
        eligible: [] as ContactEligibility[],
        excluded: [],
        rowExcludedReason: null as string | null,
      };
    }
    return computeRowSendEligibility(contactsQuery.data, locationAccountId, rowChannels);
  }, [contactsQuery.data, locationAccountId, rowChannels]);

  // Initialise toggles once the contacts load: all eligible contacts
  // checked, channels defaulted to whichever ones at least one
  // eligible contact supports.
  useEffect(() => {
    if (initialised || !contactsQuery.data) return;
    setIncludedContacts(new Set(eligible.map((e) => e.contact.id)));
    const ch = new Set<PreviewSendChannel>();
    if (eligible.some((e) => e.emailAvailable)) ch.add('email');
    if (eligible.some((e) => e.smsAvailable)) ch.add('sms');
    setChannels(ch);
    setInitialised(true);
  }, [initialised, contactsQuery.data, eligible]);

  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const sendMut = useMutation({
    mutationFn: () =>
      singleSendPreview(rolloutId, rowId, {
        channels: [...channels],
        contactIds: [...includedContacts],
      }),
    onSuccess: (result) => {
      onSent(result);
      onClose();
    },
    onError: (err: Error) => {
      setError(err instanceof ApiError ? err.message : 'Failed to queue send.');
    },
  });

  const toggleContact = (id: string) => {
    setIncludedContacts((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleChannel = (ch: PreviewSendChannel) => {
    setChannels((prev) => {
      const next = new Set(prev);
      if (next.has(ch)) next.delete(ch);
      else next.add(ch);
      return next;
    });
  };

  // Recipient count: included contacts that support at least one of
  // the selected channels.
  const recipientCount = useMemo(() => {
    return eligible.filter((e) => {
      if (!includedContacts.has(e.contact.id)) return false;
      if (channels.has('email') && e.emailAvailable) return true;
      if (channels.has('sms') && e.smsAvailable) return true;
      return false;
    }).length;
  }, [eligible, includedContacts, channels]);

  const canSend = recipientCount > 0 && channels.size > 0 && !sendMut.isPending;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <div
        className="w-full max-w-xl rounded-md bg-white shadow-xl max-h-[90vh] overflow-y-auto"
        role="dialog"
        aria-modal="true"
        aria-labelledby="send-preview-title"
      >
        <div className="p-6 space-y-4">
          <div className="space-y-1">
            <h2 id="send-preview-title" className="text-lg font-semibold text-slate-900">
              Send preview — {locationName}
            </h2>
            <p className="text-sm text-slate-500">
              Emails + SMS go to the franchisee contacts who have opted in to
              Campaign previews. Sends process in the background.
            </p>
          </div>

          {contactsQuery.isLoading && (
            <div className="py-4">
              <CenteredSpinner />
            </div>
          )}

          {contactsQuery.data && (
            <>
              {rowExcludedReason ? (
                <p className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  {rowExcludedReason}. Turn on Meta or Google for this location (or the
                  Campaign) before sending a preview.
                </p>
              ) : eligible.length === 0 ? (
                <p className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  No eligible contacts for this location. Enable “Campaign
                  previews” on a linked contact (and a comms channel) first.
                </p>
              ) : (
                <div className="space-y-3">
                  <div>
                    <p className="text-xs font-medium text-slate-700 mb-1">
                      Recipients ({eligible.length} eligible)
                    </p>
                    <ul className="space-y-1.5">
                      {eligible.map((e) => (
                        <li key={e.contact.id} className="flex items-start gap-2 text-sm">
                          <input
                            type="checkbox"
                            className="mt-0.5"
                            checked={includedContacts.has(e.contact.id)}
                            onChange={() => toggleContact(e.contact.id)}
                            id={`send-contact-${e.contact.id}`}
                          />
                          <label htmlFor={`send-contact-${e.contact.id}`} className="flex-1">
                            <span className="text-slate-900">
                              {computeDisplayName(e.contact) || '(no name)'}
                            </span>
                            <span className="ml-2 text-xs text-slate-500">
                              {e.emailAvailable && '📧'} {e.smsAvailable && '📱'}
                            </span>
                          </label>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {excluded.length > 0 && (
                    <details className="text-xs text-slate-500">
                      <summary className="cursor-pointer">
                        {excluded.length} excluded
                      </summary>
                      <ul className="mt-1 space-y-0.5 pl-4">
                        {excluded.map((x) => (
                          <li key={x.contact.id}>
                            {computeDisplayName(x.contact) || '(no name)'} —{' '}
                            <span className="italic">{x.reason}</span>
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}

                  <div>
                    <p className="text-xs font-medium text-slate-700 mb-1">
                      Channels
                    </p>
                    <div className="flex gap-4 text-sm">
                      <label className="inline-flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={channels.has('email')}
                          onChange={() => toggleChannel('email')}
                          disabled={!eligible.some((e) => e.emailAvailable)}
                        />
                        📧 Email
                      </label>
                      <label className="inline-flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={channels.has('sms')}
                          onChange={() => toggleChannel('sms')}
                          disabled={!eligible.some((e) => e.smsAvailable)}
                        />
                        📱 SMS
                      </label>
                    </div>
                  </div>

                  <p className="text-xs text-slate-500">
                    Reply-To on emails goes to your Kyck address.
                  </p>
                </div>
              )}
            </>
          )}

          {error && (
            <p className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              ref={closeRef}
              type="button"
              onClick={onClose}
              disabled={sendMut.isPending}
              className="rounded border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <Button type="button" onClick={() => sendMut.mutate()} disabled={!canSend}>
              {sendMut.isPending
                ? 'Queuing…'
                : `Send to ${recipientCount} recipient${recipientCount === 1 ? '' : 's'}`}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
